/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-library-control/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import WarningForDialog from '@components/admin/warning-for-dialog/warning-for-dialog';
import SelectWithCategoriesLov from '../../core/select-with-categories/select-with-categories-lov';
import { SelectWithSearchDataArrayInterface } from '../../core/select-with-categories/select-with-categories.interface';
import {
    AddLibraryControlErrors,
    AddLibraryControlProps,
    AddLibraryControlState,
} from './add-library-control.interface';
import AddLibraryControlReviewFrequency from './review-frequency';
import AddLibraryControlTextField from './text-field';
import { LibraryControlFieldsExtended } from '@interfaces/library-control.interface';
import { useBoolean, useTranslation } from 'hooks';
import { saveNamedItem_OpraMutations_saveNamedItem_NamedItem } from 'queries/generated/common.generated';
import {
    LibraryControlFields_LibraryControl_controlPhase_NamedItem,
    LibraryControlFields_LibraryControl_controlType_NamedItem,
    LibraryControlFields_LibraryControl_responsibleFunction_NamedItem,
} from 'queries/generated/fragments.generated';
import Constants from 'utils/constants';
import { generateReviewFrequencyUnitOptions } from 'utils/generator';
import { RiskService } from 'utils/risk.service';
import { validateFields, checkFrequencyFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import { UPDATE_LIBRARY_CONTROL, GET_LIBRARY_CONTROLS } from '@queries/legacy-risk-assessment';

const AddLibraryControl = ({
    control,
    open,
    onClose,
    organisationId,
    currentRisk,
    onAssign,
    isSystemLevel,
    controlPhases,
    controlTypes,
    responsibleFunctions,
}: AddLibraryControlProps) => {
    const { t } = useTranslation();
    const initialState: AddLibraryControlState = {
        id: null,
        description: '',
        threshold: '',
        action: '',
        reviewFrequencyUnits: 'NONE',
        reviewFrequencyNumber: 0,
        controlPhase: null,
        controlType: null,
        responsibleFunction: null,
        active: true,
    };
    const [currentControl, setCurrentControl] = useState<LibraryControlFieldsExtended | null>(null);
    const [filteredResponsibleFunctions, setFilteredResponsibleFunctions] = useState<
        saveNamedItem_OpraMutations_saveNamedItem_NamedItem[]
    >([]);
    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);
    const [state, setState] = useState<AddLibraryControlState>(initialState);
    const [errors, setErrors] = useState({} as AddLibraryControlErrors);
    const [saveLibraryControl] = useMutation(UPDATE_LIBRARY_CONTROL);

    const getReviewFrequencyTypeOptions = (): SelectWithSearchDataArrayInterface[] => {
        const reviewFrequencyTypeOptions = Object.assign([], RiskService.timeFrequencyListObject);

        reviewFrequencyTypeOptions.unshift({
            id: 'NONE',
            text: t('ra:selectReviewFrequency'),
        });

        return reviewFrequencyTypeOptions;
    };

    const getReviewFrequencyUnitOptions = (): SelectWithSearchDataArrayInterface[] =>
        generateReviewFrequencyUnitOptions(1, 31);

    useEffect(() => {
        if (open) {
            setCurrentControl(control);
            setFilteredResponsibleFunctions(
                responsibleFunctions.filter(
                    (item) => item.organisationId === organisationId && item.studyId === Constants.ObjectId.Empty,
                ),
            );
        } else if (!open) {
            disabledSave.setTrue();
            setState(initialState);
        }
    }, [open, control]);

    const setData = () => {
        if (currentControl) {
            const newControl = {
                id: currentControl.id,
                description: currentControl.description || state.description,
                controlPhase: currentControl.controlPhase || state.controlPhase,
                controlType: currentControl.controlType || state.controlType,
                responsibleFunction: currentControl.responsibleFunction || state.responsibleFunction,
                threshold: currentControl.threshold || state.threshold,
                action: currentControl.action || state.action,
                reviewFrequencyUnits: currentControl.reviewFrequencyUnits || state.reviewFrequencyUnits,
                reviewFrequencyNumber: currentControl.reviewFrequencyPeriod,
                active: currentControl.active,
            };
            setState({ ...newControl });
        }
    };

    useEffect(() => {
        if (currentControl) setData();
    }, [currentControl, open]);

    const close = (action: string | boolean | undefined) => {
        if (action !== 'error') {
            disabledSave.setTrue();
            setState({ ...initialState });
            setErrors({} as AddLibraryControlErrors);
            setCurrentControl(null);
        }
        onClose(action);
    };

    const validate = (value: string, field?: string, length?: number) => {
        const error = validateFields(value, field, length);
        const frequencyErrors =
            field === 'reviewFrequencyUnits' || field === 'reviewFrequencyNumber'
                ? checkFrequencyFields(field, value, state)
                : {};

        setErrors({
            ...errors,
            ...error,
            ...frequencyErrors,
        });
    };

    const onChange = (name: string) => (event: any) => {
        const { value } = event.target;
        const length = 3000;
        disabledSave.setFalse();

        setState({
            ...state,
            [name]: value,
        });
        validate(value, name, length);
    };

    const onChangeControlPhase = (fieldName: string) => (event: any) => {
        const { value } = event.target;
        const controlPhase = controlPhases.find(
            (type: LibraryControlFields_LibraryControl_controlPhase_NamedItem) => type.id === value,
        );
        disabledSave.setFalse();

        setState({
            ...state,
            controlPhase,
        });
        validate(value, fieldName);
    };

    const onChangeControlType = (fieldName: string) => (event: any) => {
        const { value } = event.target;
        const controlType = controlTypes.find(
            (type: LibraryControlFields_LibraryControl_controlType_NamedItem) => type.id === value,
        );

        setState({
            ...state,
            controlType,
        });

        disabledSave.setFalse();
    };

    const onChangeResponsibleFunction = (fieldName: string) => (event: any) => {
        const { value } = event.target;
        const responsibleFunction = filteredResponsibleFunctions.find(
            (item: LibraryControlFields_LibraryControl_responsibleFunction_NamedItem) => item.id === value,
        );

        setState({
            ...state,
            responsibleFunction,
        });

        disabledSave.setFalse();
    };

    const checkForRequiredFields = (): boolean => {
        const requiredFieldError = t('ra:requiredFieldError');
        const descriptionError = state.description ? '' : requiredFieldError;

        setErrors({ ...errors, description: descriptionError });

        return !descriptionError;
    };

    const saveControl = (data?: any) => {
        if (checkForRequiredFields()) {
            saving.setTrue();
            saveLibraryControl({
                variables: {
                    control: {
                        id: currentControl?.id || null,
                        organisationId,
                        description: state.description,
                        phase: currentControl
                            ? currentControl.phase === Constants.ObjectId.Empty
                                ? undefined
                                : currentControl.phase
                            : undefined,
                        controlPhaseId: state.controlPhase?.id,
                        controlTypeId: state.controlType?.id,
                        responsibleFunctionId: state.responsibleFunction?.id,
                        threshold: state.threshold,
                        action: state.action,
                        reviewFrequencyUnits: state.reviewFrequencyUnits === 'NONE' ? null : state.reviewFrequencyUnits,
                        reviewFrequencyPeriod: state.reviewFrequencyNumber,
                        active: state.active,
                        systemReferenceId: data?.id || Constants.ObjectId.Empty,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_LIBRARY_CONTROLS,
                        variables: {
                            organisationId,
                            includeSystemControls: isSystemLevel,
                        },
                    },
                ],
            })
                .then((result) => {
                    saving.setFalse();
                    if (currentRisk) onAssign(result.data.updateLibraryControl);
                    else close(currentControl ? 'edit' : 'add');
                })
                .catch(() => {
                    saving.setFalse();
                    close('error');
                });
        }
    };

    const showWarningMessage = currentControl?.associatedRisks?.length;

    const getButtonLabel = () => {
        if (currentRisk && !currentControl) return 'createAssign';
        return currentControl ? 'update' : 'add';
    };

    const descriptionField = (
        <AddLibraryControlTextField
            state={state}
            onChange={onChange('description')}
            label={`${t('description')}*`}
            id="add-library-control-description"
            fieldName="description"
            placeholder={t('ra:criticalVariableDescriptionHelperText')}
            error={errors.description}
        />
    );

    const getActionField = (className: string = '') => (
        <AddLibraryControlTextField
            state={state}
            onChange={onChange('action')}
            className={className}
            label={t('ra:actionToBeTaken')}
            id="add-library-control-action-to-be-taken"
            fieldName="action"
            placeholder={t('ra:enterActionToBeTaken')}
            error={errors.action}
        />
    );

    const thresholdField = (
        <AddLibraryControlTextField
            state={state}
            onChange={onChange('threshold')}
            label={t('ra:threshold')}
            id="add-library-control-threshold"
            fieldName="threshold"
            placeholder={t('ra:enterThreshold')}
            error={errors.threshold}
        />
    );

    const getReviewFrequencyFields = (className: string = '') => (
        <AddLibraryControlReviewFrequency
            className={className}
            onChange={onChange}
            reviewFrequencyTypeOptions={getReviewFrequencyTypeOptions()}
            state={state}
            errors={errors}
            reviewFrequencyUnitOptions={getReviewFrequencyUnitOptions()}
        />
    );

    const systemDialogContent = (
        <>
            {descriptionField}
            {getActionField()}
            {thresholdField}
            {getReviewFrequencyFields('before-select')}
        </>
    );

    const orgDialogContent = (
        <>
            <div className="type-edc-wrapper mb-30">
                <FormControl variant="outlined" className="form-control-type not-full mr">
                    <SelectWithCategoriesLov
                        label={`${t('ra:phase')}`}
                        onChange={onChangeControlPhase}
                        namedItems={controlPhases}
                        labelField="controlPhase"
                        fieldSelect="controlPhase"
                        customId="add-library-control-phase"
                        customClassName="add-library-control-phase"
                        error={errors.controlPhase}
                        currentItem={state.controlPhase}
                        initialState={control?.controlPhase?.id}
                        defaultText={t('admin:selectValue')}
                    />
                </FormControl>
            </div>
            <div className="type-edc-wrapper mb-30">
                <FormControl variant="outlined" className="form-control-type mr">
                    <SelectWithCategoriesLov
                        label={t('admin:controlType')}
                        onChange={onChangeControlType}
                        namedItems={controlTypes}
                        labelField="controlType"
                        fieldSelect="controlType"
                        customId="add-library-control-controlType"
                        customClassName="add-library-control-controlType"
                        error={errors.controlType}
                        currentItem={state.controlType}
                        initialState={control?.controlType?.id}
                        defaultText={t('admin:selectValue')}
                    />
                </FormControl>
                <FormControl variant="outlined" className="form-control-type">
                    <SelectWithCategoriesLov
                        label={t('ra:responsibleFunction')}
                        onChange={onChangeResponsibleFunction}
                        namedItems={filteredResponsibleFunctions}
                        labelField="responsibleFunction"
                        fieldSelect="responsibleFunction"
                        customId="add-library-control-responsibleFunction"
                        customClassName="add-library-control-responsibleFunction"
                        error={errors.responsibleFunction}
                        currentItem={state.responsibleFunction}
                        initialState={control?.responsibleFunction?.id}
                        defaultText={t('ra:selectResponsibleFunction')}
                        grouped
                    />
                </FormControl>
            </div>
            {getReviewFrequencyFields('mb-30')}
            {descriptionField}
            {thresholdField}
            {getActionField('before-select')}
        </>
    );

    if (!open) return null;

    return (
        <CustomDialog className="create-controls-dialog" onClose={() => {}} fullWidth maxWidth="md" scroll="paper" open>
            <DialogTitle id="add-library-control-modal-title">
                {currentControl
                    ? t('ra:editControl', { controlId: currentControl?.recordId || currentControl?.id })
                    : t('ra:addControl')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close(false);
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent
                style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 20 }}
                className={cn({ warning: showWarningMessage })}
            >
                {!!showWarningMessage && (
                    <div className="wrap-warning-message-absolute">
                        <WarningForDialog
                            firstNumber={0}
                            secondNumber={currentControl?.associatedRisks?.length}
                            type="control"
                        />
                    </div>
                )}
                <div className="type-edc-wrapper">
                    <Checkbox
                        rounded="none"
                        className="cell-checkbox no-full-width flex-start no-border"
                        id="active-control-checkbox"
                        checked={state.active}
                        onChange={() => {
                            disabledSave.setFalse();
                            setState({
                                ...state,
                                active: !state.active,
                            });
                        }}
                        label={t('active')}
                        data-testid="active-control-checkbox"
                        size="medium"
                    />
                </div>
                {isSystemLevel ? systemDialogContent : orgDialogContent}
                <DialogActions
                    style={{ paddingTop: 20, paddingBottom: 10 }}
                    className={`actions-info-wrapper-edit-cv ${!currentControl ? 'flex-end' : ''}`}
                >
                    <ModalUpdatedInfo item={currentControl} />
                    <Button
                        disabled={
                            Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                            disabledSave.value ||
                            saving.value
                        }
                        text={getButtonLabel()}
                        id="edit-control-save-close"
                        onClick={saveControl}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default AddLibraryControl;
